import { memo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/electron/renderer'
import { useGatewayChannel } from '@renderer/hooks/useGatewayChannel'
import {
  useSetStagedActionJobs,
  STAGED_ACTIONS_JOBS_QUERY_KEY,
  type StagedActionJob,
} from '@renderer/overlay/hooks/use-staged-action-jobs'
import { STAGED_ACTIONS_QUERY_KEY } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { FOR_YOU_CHATS_QUERY_KEY, HISTORY_ACTIVITY_QUERY_KEY } from '@renderer/stores/conversation-query-keys'
import { reconcileJobStatusSnapshot } from './staged-action-job-status'

export const StagedActionsGatewayProvider = memo(({ children }: React.PropsWithChildren) => {
  const queryClient = useQueryClient()
  const setStagedActionJobs = useSetStagedActionJobs()
  const lastJobStatusPublishedAtRef = useRef<number | undefined>(undefined)

  const invalidateActionQueries = (): void => {
    void Promise.all([
      queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: [HISTORY_ACTIVITY_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: [FOR_YOU_CHATS_QUERY_KEY] }),
    ]).catch((error) => Sentry.captureException(error))
  }

  useGatewayChannel('staged-actions', (payload) => {
    if (payload.action === 'job-status') {
      const prevJobs = queryClient.getQueryData<Array<StagedActionJob>>([STAGED_ACTIONS_JOBS_QUERY_KEY])
      const result = reconcileJobStatusSnapshot(prevJobs, lastJobStatusPublishedAtRef.current, payload)
      if (result.stale) {
        return
      }
      lastJobStatusPublishedAtRef.current = result.lastPublishedAt
      setStagedActionJobs(result.jobs)
      if (result.aJobFinished) {
        invalidateActionQueries()
      }
      return
    }

    if (payload.action === 'resolved' || payload.action === 'unresolved' || payload.action === 'updated') {
      invalidateActionQueries()
    }
  })

  return <>{children}</>
})
StagedActionsGatewayProvider.displayName = 'StagedActionsGatewayProvider'
